/**
 * Server-Sent Events helpers.
 *
 * (a) {@link streamSSE} – POST-initiated streams (e.g. "send chat message and
 *     stream the reply"). Uses fetch + ReadableStream so we can send a JSON body,
 *     cookies and the `X-Tenant-Id` header, and abort at any time.
 *
 * (b) {@link subscribeSSE} – long-lived GET subscriptions (e.g. live run events)
 *     with EventSource semantics: automatic reconnect with backoff, honours the
 *     server's `retry:` hint and resends `Last-Event-ID`. Implemented on fetch
 *     instead of `EventSource` because EventSource cannot send the tenant header.
 *
 * Both share {@link SseParser}, a spec-compliant incremental frame parser.
 */
import { baseHeaders, buildUrl, type QueryParams, toApiError } from "@/api/client"

export interface SseEvent<T = unknown> {
  /** `event:` field; defaults to "message". */
  event: string
  /** Raw `data:` payload (multi-line data joined with "\n"). */
  data: string
  /** `data` parsed as JSON when possible, otherwise the raw string. */
  json: T
  id?: string
  retry?: number
}

function parseJson(data: string): unknown {
  if (data === "") return data
  try {
    return JSON.parse(data)
  } catch {
    return data
  }
}

/**
 * Incremental SSE parser (WHATWG "event stream interpretation").
 * Feed it decoded text chunks of arbitrary size; it returns completed events.
 */
export class SseParser {
  private buffer = ""
  private dataLines: string[] = []
  private eventType = ""
  private lastId: string | undefined
  private retry: number | undefined
  private sawField = false

  /** The most recent `id:` seen (used for Last-Event-ID on reconnect). */
  get lastEventId() {
    return this.lastId
  }

  feed(chunk: string): SseEvent[] {
    this.buffer += chunk
    const events: SseEvent[] = []
    // split on CRLF, LF or CR; keep the trailing partial line in the buffer
    for (;;) {
      const match = /\r\n|\n|\r/.exec(this.buffer)
      if (!match) break
      // a lone trailing "\r" might be the first half of "\r\n" – wait for more data
      if (match[0] === "\r" && match.index === this.buffer.length - 1) break
      const line = this.buffer.slice(0, match.index)
      this.buffer = this.buffer.slice(match.index + match[0].length)
      const ev = this.processLine(line)
      if (ev) events.push(ev)
    }
    return events
  }

  /** Flush a final event that was not terminated by a blank line (stream ended). */
  flush(): SseEvent[] {
    const out: SseEvent[] = []
    if (this.buffer) {
      const ev = this.processLine(this.buffer)
      this.buffer = ""
      if (ev) out.push(ev)
    }
    const last = this.dispatch()
    if (last) out.push(last)
    return out
  }

  private processLine(line: string): SseEvent | null {
    if (line === "") return this.dispatch()
    if (line.startsWith(":")) return null // comment / keep-alive
    const colon = line.indexOf(":")
    const field = colon === -1 ? line : line.slice(0, colon)
    let value = colon === -1 ? "" : line.slice(colon + 1)
    if (value.startsWith(" ")) value = value.slice(1)
    switch (field) {
      case "event":
        this.eventType = value
        this.sawField = true
        break
      case "data":
        this.dataLines.push(value)
        this.sawField = true
        break
      case "id":
        if (!value.includes("\0")) this.lastId = value
        break
      case "retry":
        if (/^\d+$/.test(value)) this.retry = Number(value)
        break
      default:
        break
    }
    return null
  }

  private dispatch(): SseEvent | null {
    if (!this.sawField) {
      this.eventType = ""
      return null
    }
    const data = this.dataLines.join("\n")
    const ev: SseEvent = {
      event: this.eventType || "message",
      data,
      json: parseJson(data),
      id: this.lastId,
      retry: this.retry,
    }
    this.dataLines = []
    this.eventType = ""
    this.sawField = false
    return ev
  }
}

/** Read a streaming Response body and yield SSE events as they arrive. */
export async function* readSseStream(
  body: ReadableStream<Uint8Array>,
  parser = new SseParser(),
): AsyncGenerator<SseEvent> {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      for (const ev of parser.feed(decoder.decode(value, { stream: true }))) yield ev
    }
    for (const ev of parser.feed(decoder.decode())) yield ev
    for (const ev of parser.flush()) yield ev
  } finally {
    reader.releaseLock()
  }
}

export interface StreamOptions {
  method?: "POST" | "GET" | "PUT" | "PATCH"
  body?: unknown
  query?: QueryParams
  signal?: AbortSignal
  tenantId?: number | null
}

/**
 * Open a POST-initiated SSE stream and iterate its events.
 *
 * ```ts
 * const ctrl = new AbortController()
 * for await (const ev of streamSSE("/chat/stream", { body: { message }, signal: ctrl.signal })) {
 *   if (ev.event === "text_delta") append(ev.json)
 * }
 * ```
 * Throws {@link ApiError} for non-2xx responses (body is parsed as an API error).
 * Aborting the signal ends iteration with an `AbortError`.
 */
export async function* streamSSE<T = unknown>(
  path: string,
  opts: StreamOptions = {},
): AsyncGenerator<SseEvent<T>> {
  const headers = baseHeaders({ tenantId: opts.tenantId })
  headers.Accept = "text/event-stream"
  let payload: BodyInit | undefined
  if (opts.body instanceof FormData) payload = opts.body
  else if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json"
    payload = JSON.stringify(opts.body)
  }
  const res = await fetch(buildUrl(path, opts.query), {
    method: opts.method ?? "POST",
    headers,
    body: payload,
    credentials: "same-origin",
    signal: opts.signal,
  })
  if (!res.ok) throw await toApiError(res, path)
  if (!res.body) return
  yield* readSseStream(res.body) as AsyncGenerator<SseEvent<T>>
}

export type SubscriptionStatus = "connecting" | "open" | "reconnecting" | "closed"

export interface SubscribeOptions<T = unknown> {
  query?: QueryParams
  tenantId?: number | null
  onEvent: (event: SseEvent<T>) => void
  onStatus?: (status: SubscriptionStatus) => void
  onError?: (error: unknown) => void
  /** Initial reconnect delay (ms). Doubles per failed attempt up to `maxRetryMs`. */
  retryMs?: number
  maxRetryMs?: number
  /** Stop reconnecting after this many consecutive failures (default: never). */
  maxAttempts?: number
}

export interface Subscription {
  close: () => void
}

/**
 * Subscribe to a GET event stream with automatic reconnect.
 * Returns a handle; call `close()` to stop (also stops reconnecting).
 * Client errors (4xx except 408/429) are treated as fatal and stop the loop.
 */
export function subscribeSSE<T = unknown>(path: string, opts: SubscribeOptions<T>): Subscription {
  const ctrl = new AbortController()
  const baseDelay = opts.retryMs ?? 1000
  const maxDelay = opts.maxRetryMs ?? 30_000
  let serverRetry: number | undefined
  let lastEventId: string | undefined
  let attempts = 0
  let closed = false

  const setStatus = (s: SubscriptionStatus) => opts.onStatus?.(s)

  const sleep = (ms: number) =>
    new Promise<void>((resolve) => {
      const t = setTimeout(resolve, ms)
      ctrl.signal.addEventListener(
        "abort",
        () => {
          clearTimeout(t)
          resolve()
        },
        { once: true },
      )
    })

  const run = async () => {
    setStatus("connecting")
    while (!closed) {
      try {
        const headers = baseHeaders({ tenantId: opts.tenantId })
        headers.Accept = "text/event-stream"
        if (lastEventId) headers["Last-Event-ID"] = lastEventId
        const res = await fetch(buildUrl(path, opts.query), {
          headers,
          credentials: "same-origin",
          signal: ctrl.signal,
          cache: "no-store",
        })
        if (!res.ok) {
          const err = await toApiError(res, path)
          const fatal = res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429
          if (fatal) {
            opts.onError?.(err)
            break
          }
          throw err
        }
        if (!res.body) throw new Error("Empty event stream")
        attempts = 0
        setStatus("open")
        const parser = new SseParser()
        for await (const ev of readSseStream(res.body, parser)) {
          if (ev.retry !== undefined) serverRetry = ev.retry
          if (ev.id !== undefined) lastEventId = ev.id
          opts.onEvent(ev as SseEvent<T>)
        }
        // stream ended normally → reconnect like EventSource does
      } catch (e) {
        if (closed || (e instanceof DOMException && e.name === "AbortError")) break
        opts.onError?.(e)
      }
      if (closed) break
      attempts += 1
      if (opts.maxAttempts !== undefined && attempts > opts.maxAttempts) break
      setStatus("reconnecting")
      const backoff = Math.min(maxDelay, (serverRetry ?? baseDelay) * 2 ** Math.max(0, attempts - 1))
      await sleep(backoff * (0.8 + Math.random() * 0.4))
    }
    setStatus("closed")
  }

  void run()

  return {
    close: () => {
      closed = true
      ctrl.abort()
    },
  }
}
